import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import MicOffIcon from '@mui/icons-material/MicOff';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Avatar, Box, Button, IconButton, Menu, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { MotionContainer, varBounceInDown } from '../../components/animate';
import ChatEncerradoAlert from '../../components/ChatEncerradoAlert';
import ChatReconectandoAlert from '../../components/ChatReconectandoAlert';
import ChatInatividade from '../../components/Chatinatividade';
import { useWSContext } from '../../contexts/WebSocketContext';
import { getSupportAgents } from '../../services/suporte-service/supportService';

function formatAgentName(fullName) {
  const raw = String(fullName || '').trim().replace(/\s+/g, ' ');
  if (!raw) return 'Suporte';
  const parts = raw.split(' ');
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1]}`;
}

function ChatHead({ recebeEncerraChat, supportIsWriting }) {
  const theme = useTheme();

  const {
    supportName,
    supportLeftMessage,
    alertState,
    timer,
    audioDisabledByDevice,
    handleClientEndChat,
    reconnectionStatus
  } = useWSContext();

  const [agentes, setAgentes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fotoPersistida, setFotoPersistida] = useState(() => localStorage.getItem('fotoSuporte'));
  const [nomePersistido, setNomePersistido] = useState(() => localStorage.getItem('nomeSuporte'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [showAudioDisabledAlert, setShowAudioDisabledAlert] = useState(false);
  const audioNoticeShownRef = useRef(false);

  useEffect(() => {
    const fetchAtendenteInfo = async () => {
      try {
        const data = await getSupportAgents();
        if (data && data.length > 0) {
          setAgentes(data);
        }
      } catch (err) {
        console.error('Falha ao buscar dados do atendente:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAtendenteInfo();
  }, []);

  useEffect(() => {
    if (agentes.length > 0 && supportName) {
      const agenteCorreto = agentes.find(agente => agente.nome.includes(supportName));
      if (agenteCorreto) {
        setFotoPersistida(agenteCorreto.foto);
        setNomePersistido(agenteCorreto.nome);
        localStorage.setItem('fotoSuporte', agenteCorreto.foto);
        localStorage.setItem('nomeSuporte', agenteCorreto.nome);
      }
    }
  }, [agentes, supportName]);

  useEffect(() => {
    if (!audioDisabledByDevice) {
      setShowAudioDisabledAlert(false);
      audioNoticeShownRef.current = false;
      return undefined;
    }
    if (audioNoticeShownRef.current) return undefined;

    audioNoticeShownRef.current = true;
    setShowAudioDisabledAlert(true);
    const timerId = setTimeout(() => setShowAudioDisabledAlert(false), 9000);
    return () => clearTimeout(timerId);
  }, [audioDisabledByDevice]);

  const isOnline = (supportName || nomePersistido) && !supportLeftMessage;
  const displaySupportName = formatAgentName(nomePersistido || supportName);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEncerrarChat = () => {
    handleMenuClose();
    handleClientEndChat();
    if (recebeEncerraChat) {
      recebeEncerraChat(true);
    }
  };

  const getEncerramentoMessage = () => {
    if (typeof supportLeftMessage === 'string' && supportLeftMessage) {
      return supportLeftMessage;
    }
    return 'A conexão foi encerrada.';
  };

  const renderAlerts = () => {
    if (alertState === 'terminated') {
      return <ChatEncerradoAlert mensagem={getEncerramentoMessage()} />;
    }

    // Prioridade 2: Alertas de reconexão bem-sucedida (só aparecem se o chat NÃO foi encerrado)
    if (reconnectionStatus === 'returning') {
      return (
        <ChatReconectandoAlert
          mensagem='O suporte está retornando ao atendimento...'
          severity="info"
          icon={<InfoOutlinedIcon />}
        />
      );
    }
    if (reconnectionStatus === 'returned') {
      return (
        <ChatReconectandoAlert
          mensagem='O suporte retornou!'
          severity="success"
          icon={<CheckCircleOutlineOutlinedIcon />}
        />
      );
    }


    switch (alertState) {
      case 'reconnecting':
        return <ChatReconectandoAlert mensagem='Tentando reconectar ao seu atendimento...' timer={timer} severity="warning" icon={false} />;
      case 'temporary':
        return <ChatReconectandoAlert mensagem='O suporte perdeu conexão. Aguardando...' timer={timer} severity="warning" icon={false} />;
      default:
        if (audioDisabledByDevice && showAudioDisabledAlert) {
          return (
            <ChatReconectandoAlert
              mensagem='Mensagem de voz e video indisponiveis neste aparelho por limitacao de hardware de midia. Nesta conversa, use texto, foto ou PDF.'
              severity="info"
              icon={(
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  <VolumeOffIcon fontSize="small" />
                  <MicOffIcon fontSize="small" />
                </Box>
              )}
            />
          );
        }
        return <ChatInatividade />;
    }
  };


  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          p: 2,
          justifyContent: 'space-between',
          alignItems: 'center',
          background: theme.palette.background.default,
          boxShadow: theme.shadows[1],
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar
              alt={displaySupportName}
              src={fotoPersistida?.startsWith('http') ? fotoPersistida : undefined}
              sx={{ width: 56, height: 56, background: theme.palette.primary.main, color: theme.palette.primary.contrastText, position: 'relative', fontSize: '1.75rem' }}
            >
              {displaySupportName.charAt(0)}
            </Avatar>
            <Box sx={{ position: 'relative', marginLeft: 2, width: 12, height: 12 }}>
              {isOnline && (
                <motion.div
                  animate={{ scale: [1, 1.4], opacity: [1, 0] }}
                  transition={{ repeat: Infinity, duration: 1, ease: 'easeOut', repeatDelay: 0.5 }}
                  style={{ position: 'absolute', top: 21, left: -33, width: '140%', height: '140%', borderRadius: '50%', border: '1.6px solid rgb(7, 197, 0)', backgroundColor: 'transparent', transform: 'translate(-50%, -50%)' }}
                />
              )}
              <Box sx={{ width: 14, height: 14, left: -31, top: 22, borderRadius: '50%', backgroundColor: isOnline ? '#32CD32' : 'rgb(231, 231, 231)', boxShadow: `0px 0px 8px ${isOnline ? '#66bb6a' : 'rgb(114, 114, 114)'}`, position: 'relative', zIndex: 1 }} />
            </Box>
            <Box sx={{ marginLeft: -2 }}>
              <Typography variant="h5">
                {displaySupportName}
              </Typography>
              {isOnline ? (
                <Typography variant="body2" sx={{ color: 'gray' }}>
                  {supportIsWriting ? 'Suporte está digitando...' : 'Online'}
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ color: 'gray' }}>
                  Offline
                </Typography>
              )}
            </Box>
          </Box>
        <Box>
          <IconButton onClick={handleMenuOpen}>
            <MoreVertIcon sx={{ color: 'black' }} />
          </IconButton>
          <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
            <Button fullWidth color="error" onClick={handleEncerrarChat}>
              Encerrar Conversa
            </Button>
          </Menu>
        </Box>
      </Box>

      <MotionContainer initial="initial" open>
        <motion.div variants={varBounceInDown}>
          {renderAlerts()}
        </motion.div>
      </MotionContainer>
    </Box>
  );
}

export default ChatHead;
